/* eslint-disable react-refresh/only-export-components */
import React from "react";
import {
  APIError,
  Configuration,
  DefaultApi,
  NotOKResponseError,
  ResponseError,
  createAuthenticatedAPI,
} from "@norma-bi/bi-api";
import { BASE_URL } from "../constants";
import { useAccessToken } from "../Auth/useAccessToken";
import * as Sentry from "@sentry/react";
import { AuthenticatedAPI, RootAPI, ThoughtSpotAPI, UnauthenticatedAPI } from "./apis";
import { useThoughtSpotToken } from "@/thoughtspot";

async function reportError(err: APIError) {
  console.error("api error: ", err, JSON.stringify(err));
  const resp =
    err instanceof NotOKResponseError || err instanceof ResponseError
      ? err.response
      : err.context.response;
  if (!resp) {
    Sentry.captureException(
      new Error("api error occurred but no response was included: " + String(err)),
    );
    return;
  }

  let body: string | null = null;
  try {
    body = await resp.text();
  } catch (e) {
    console.error("could not get response.text()", e);
    body = "could not get response.text()";
  }
  Sentry.captureEvent({
    level: "error",
    message: "Not ok response",
    tags: {
      url: resp.url,
      status: resp.status,
    },
    extra: {
      body,
    },
  });
}

export type APIs = {
  unauthenticatedAPI: UnauthenticatedAPI;
  rootAPI: RootAPI | null;
  authenticatedAPI: AuthenticatedAPI | null;
  thoughtSpotAPI: ThoughtSpotAPI | null;
};

export const apisContext = React.createContext<APIs>({
  unauthenticatedAPI: new DefaultApi(new Configuration({ basePath: BASE_URL })),
  rootAPI: null,
  authenticatedAPI: null,
  thoughtSpotAPI: null,
});

export function APIsProvider(props: React.PropsWithChildren) {
  const { accessToken } = useAccessToken();
  const { token: thoughtspotToken } = useThoughtSpotToken();

  const apis = React.useMemo<APIs>(() => {
    const authenticatedAPI = accessToken
      ? createAuthenticatedAPI({
          accessToken,
          basePath: BASE_URL,
          onError: reportError,
        })
      : null;

    return {
      unauthenticatedAPI: new DefaultApi(new Configuration({ basePath: BASE_URL })),
      rootAPI: authenticatedAPI,
      authenticatedAPI: authenticatedAPI,
      thoughtSpotAPI: null,
    };
  }, [accessToken]);

  const thoughspotAPI = React.useMemo(() => {
    return accessToken && thoughtspotToken
      ? createAuthenticatedAPI({
          accessToken,
          basePath: BASE_URL,
          onError: reportError,
          headers: new Headers({
            "X-THOUGHTSPOT-TOKEN": thoughtspotToken,
          }),
        })
      : null;
  }, [accessToken, thoughtspotToken]);

  return (
    <apisContext.Provider
      value={{
        ...apis,
        thoughtSpotAPI: thoughspotAPI,
      }}
    >
      {props.children}
    </apisContext.Provider>
  );
}

export function useAPIs() {
  return React.useContext(apisContext);
}

export function useUnauthenticatedAPI() {
  const { unauthenticatedAPI } = useAPIs();
  return unauthenticatedAPI;
}

export function useAuthenticatedAPI() {
  const { authenticatedAPI } = useAPIs();
  if (!authenticatedAPI) {
    throw new Error("Authenticated API is not available");
  }
  return authenticatedAPI;
}

export function useThoughtSpotAPI() {
  const { thoughtSpotAPI } = useAPIs();
  if (!thoughtSpotAPI) {
    throw new Error("ThoughtSpot API is not available");
  }
  return thoughtSpotAPI;
}

export function useRootAPI() {
  const { rootAPI } = useAPIs();
  if (!rootAPI) {
    throw new Error("Root API is not available");
  }
  return rootAPI;
}
